import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'
import { breadcrumb } from './breadcrumbs.ts'
import { apiUrl, isTauri } from './platform.ts'
import { getSessionToken } from './session-token.ts'

export interface Me {
  id: string
  name: string
  email: string
  locale: string
  timezone: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

/** Raw authenticated fetch against the api origin (cookie on web, bearer in Tauri). */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  const token = await getSessionToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (typeof init.body === 'string' && !headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json')
  return fetch(apiUrl(url), { ...init, headers, credentials: isTauri ? 'omit' : 'same-origin' })
}

async function toError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null
  return new ApiError(res.status, body?.error?.code ?? 'unknown', body?.error?.message ?? res.statusText)
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authFetch(`/api${path}`, init)
  if (!res.ok) {
    breadcrumb(`HTTP ${res.status} ${init.method ?? 'GET'} ${path.split('?')[0]}`)
    throw await toError(res)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** JSON request helper: `api.post('/v1/notes', body)` */
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T = void>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const res = await authFetch(`/api${path}`, { method: 'POST', body: form })
    if (!res.ok) throw await toError(res)
    return (await res.json()) as T
  },
  blob: async (path: string): Promise<Blob> => {
    const res = await authFetch(`/api${path}`)
    if (!res.ok) throw await toError(res)
    return res.blob()
  },
}

/** Network failure, server-side error or expired session: worth retrying later (offline queue). */
export function isRetryable(e: unknown): boolean {
  if (e instanceof ApiError)
    return e.status >= 500 || e.status === 401 || e.status === 408 || e.status === 429
  return true // TypeError: failed to fetch, aborted, …
}

/** Current user, or null when not signed in. */
/**
 * After a sign-in the cached "not signed in" (null) must not survive: route guards use ensureQueryData,
 * which returns stale cache without refetching. Drop it and load the session fresh.
 */
export async function refreshSession(queryClient: QueryClient): Promise<Me | null> {
  queryClient.removeQueries({ queryKey: ['me'] })
  return queryClient.fetchQuery(meQuery)
}

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<Me | null> => {
    try {
      return (await apiFetch<{ user: Me }>('/v1/me')).user
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null
      throw e
    }
  },
  staleTime: 5 * 60_000,
})
